import type { FilterOperator, FilterState, FilterValue } from "./types";
import {
  isPresetValue,
  resolveDatePreset,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
import { at } from "#app/lib/utils/at";

/**
 * Convert a "last updated" preset descriptor into a DateTimeFilter condition.
 * Resolves the preset to a {startDate, endDate} window and emits
 * { lastUpdateDate: { gte: startDate, lte: endDate } }.
 */
function convertLastUpdateDateFilter(
  preset: DatePresetValue
): Record<string, unknown> | null {
  if (preset.key === "custom" && !preset.custom) return null;
  const { startDate, endDate } = resolveDatePreset(preset.key, preset.custom);
  if (!startDate || !endDate) return null;
  return { lastUpdateDate: { gte: startDate, lte: endDate } };
}

// Operators that don't take a value
const NO_VALUE_OPERATORS: FilterOperator[] = [
  "isNull",
  "isNotNull",
  "isEmpty",
  "isNotEmpty",
];

// Operators that expect array values
const ARRAY_VALUE_OPERATORS: FilterOperator[] = [
  "in",
  "nin",
  "hasSome",
  "hasEvery",
];

/**
 * Build a GraphQL filter value from an operator and value.
 *
 * Translates logical operators like `isNull` into their Prisma equivalents
 * (e.g., `{ eq: null }`), and passes through standard operators directly.
 */
function buildFilterValue(
  operator: FilterOperator,
  value: unknown
): Record<string, unknown> | null {
  if (NO_VALUE_OPERATORS.includes(operator)) {
    switch (operator) {
      case "isNull":
        return { eq: null };
      case "isNotNull":
        return { ne: null };
      case "isEmpty":
        return { isEmpty: true };
      case "isNotEmpty":
        return { isEmpty: false };
      default:
        return null;
    }
  }

  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (ARRAY_VALUE_OPERATORS.includes(operator)) {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }
    return { [operator]: value };
  }

  return { [operator]: value };
}

/**
 * Build a nested filter structure from a dot-notation field path.
 *
 * e.g., "assignees.entityId" with `{ hasSome: ["id1"] }`
 * becomes `{ assignees: { entityId: { hasSome: ["id1"] } } }`
 */
function buildNestedFilter(
  fieldPath: string,
  filterValue: Record<string, unknown>
): Record<string, unknown> {
  const parts = fieldPath.split(".");

  if (parts.length === 1) {
    return { [fieldPath]: filterValue };
  }

  let result: Record<string, unknown> = filterValue;
  for (const part of [...parts].reverse()) {
    result = { [part]: result };
  }
  return result;
}

/**
 * Process a single FilterValue into a GraphQL filter object.
 */
function processFilter(
  filter: FilterValue
): Record<string, unknown> | null {
  const { field, operator, value } = filter;

  if (!field || !operator) {
    return null;
  }

  // Special case: "lastUpdateDate" quick-filter stores a preset descriptor
  // ({ key, custom? }) that resolves to a startDate/endDate range.
  if (field === "lastUpdateDate" && isPresetValue(value)) {
    return convertLastUpdateDateFilter(value);
  }

  // Special case: "workflow-stage" stores a composite { workflowIds, stageIds }
  // that must be decomposed into separate workflowId/stageId conditions.
  if (field === "workflow-stage" && value && typeof value === "object" && !Array.isArray(value)) {
    const { workflowIds, stageIds } = value as { workflowIds: string[]; stageIds: string[] };
    const conditions: Record<string, unknown>[] = [];
    if (workflowIds?.length) conditions.push({ workflowId: { in: workflowIds } });
    if (stageIds?.length) conditions.push({ stageId: { in: stageIds } });
    if (conditions.length === 0) return null;
    if (conditions.length === 1) return at(conditions, 0, "condition");
    return { AND: conditions };
  }

  const filterValue = buildFilterValue(operator, value);
  if (!filterValue) {
    return null;
  }

  return buildNestedFilter(field, filterValue);
}

/**
 * Convert a FilterState into a GraphQL WhereInput object.
 *
 * Each block's filters are combined with the block's logic (AND/OR).
 * Multiple blocks are combined with the main logic operator.
 */
export function buildWhereFromState(
  state: FilterState
): Record<string, unknown> | undefined {
  if (!state.blocks.length) {
    return undefined;
  }

  const processedBlocks = state.blocks
    .map((block) => {
      const conditions = block.filters
        .map(processFilter)
        .filter(Boolean) as Record<string, unknown>[];

      if (conditions.length === 0) {
        return null;
      }

      if (conditions.length === 1) {
        return conditions[0];
      }

      const blockOp = block.logic === "OR" ? "OR" : "AND";
      return { [blockOp]: conditions };
    })
    .filter(Boolean) as Record<string, unknown>[];

  if (processedBlocks.length === 0) {
    return undefined;
  }

  if (processedBlocks.length === 1) {
    return processedBlocks[0];
  }

  const mainOp = state.logic === "OR" ? "OR" : "AND";
  return { [mainOp]: processedBlocks };
}

/**
 * Count the total number of active filters in a FilterState.
 */
export function countActiveFilters(state: FilterState): number {
  let count = 0;
  for (const block of state.blocks) {
    for (const filter of block.filters) {
      if (!filter.field || !filter.operator) continue;
      if (NO_VALUE_OPERATORS.includes(filter.operator)) {
        count++;
      } else if (filter.value !== null && filter.value !== undefined && filter.value !== "") {
        if (Array.isArray(filter.value)) {
          if (filter.value.length > 0) count++;
        } else if (filter.field === "workflow-stage" && typeof filter.value === "object") {
          const { workflowIds, stageIds } = filter.value as { workflowIds: string[]; stageIds: string[] };
          if (workflowIds?.length || stageIds?.length) count++;
        } else {
          count++;
        }
      }
    }
  }
  return count;
}
